import { ThemeEvents } from '@theme/events';

/**
 * Soodam delivery-estimate resolver.
 *
 * Upgrades every `[data-delivery-estimate]` node — the cart line and the
 * product page line under the add-to-cart button — from the cache-safe
 * server fallback ("Arrives in 2 to 10 business days") to a concrete
 * calendar range ("Arrives Jun 11 – 18"), computed from the VISITOR'S clock.
 *
 * Why client-side: the storefront HTML is served from Shopify's full-page
 * cache, so a Liquid-computed "today" can be days stale. The window is
 * `data-min-days`..`data-max-days` BUSINESS days (weekends skipped); the
 * total = prep + transit, set server-side in
 * `snippets/soodam-delivery-estimate.liquid`.
 *
 * Loaded once per page from `snippets/scripts.liquid` so the listener
 * survives section morphs (morphed sections do not run inline scripts). The
 * estimate doesn't depend on cart contents, but the cart drawer/page morph
 * re-injects the server fallback, so we re-resolve on `cart:update` too.
 */

const SELECTOR = '[data-delivery-estimate]';
const NBSP_DASH = ' – '; // thin space + en dash + thin space

const locale = document.documentElement.lang || 'en';
const fmtMonthDay = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
const fmtDay = new Intl.DateTimeFormat(locale, { day: 'numeric' });
const fmtLong = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' });

/**
 * Advance `count` business days from `start`, skipping Saturdays and Sundays.
 * An order placed on a weekend naturally lands its first business day on the
 * following Monday, since only weekdays are counted.
 * @param {Date} start
 * @param {number} count
 * @returns {Date}
 */
function addBusinessDays(start, count) {
  const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  let added = 0;
  while (added < count) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
}

/**
 * @param {HTMLElement} node
 */
function resolveNode(node) {
  const minDays = Number(node.dataset.minDays);
  const maxDays = Number(node.dataset.maxDays);
  if (!minDays || !maxDays || maxDays < minDays) return;

  const valueEl = node.querySelector('[data-delivery-value]');
  if (!valueEl) return;

  const today = new Date();
  const earliest = addBusinessDays(today, minDays);
  const latest = addBusinessDays(today, maxDays);

  const sameMonth =
    earliest.getMonth() === latest.getMonth() &&
    earliest.getFullYear() === latest.getFullYear();

  // "Jun 11 – 18" when the window stays in one month; "Jun 28 – Jul 5" when
  // it crosses one.
  const start = fmtMonthDay.format(earliest);
  const end = sameMonth ? fmtDay.format(latest) : fmtMonthDay.format(latest);
  const visible = `${start}${NBSP_DASH}${end}`;

  if (valueEl.textContent === visible) return; // already resolved, skip reflow

  valueEl.textContent = visible;
  // Spell the range out for screen readers; the en dash reads inconsistently.
  node.setAttribute(
    'aria-label',
    `Arrives between ${fmtLong.format(earliest)} and ${fmtLong.format(latest)}.`
  );
  node.dataset.resolved = 'true';
}

function resolveAll() {
  for (const node of document.querySelectorAll(SELECTOR)) {
    if (node instanceof HTMLElement) resolveNode(node);
  }
}

// Re-resolve whenever an estimate node is touched in the DOM. The cart drawer
// and /cart page render their summary via Shopify's Section Rendering API,
// which morphs fresh server HTML (carrying the business-day FALLBACK) on open
// and on every cart:update. Two morph shapes have to be covered:
//   - a node is inserted/replaced  → childList mutation
//   - the morph patches the value's TEXT in place (morphdom-style, reusing the
//     existing node) → characterData mutation, NO childList event
// The in-place text patch is what reverted the resolved date back to the
// fallback in the cart. The morph also lands after the cart:update event
// fires, so listening to that event alone races it. Observing mutations is
// timing-independent. resolveNode() early-returns when the text already holds
// the resolved range, so re-setting it (which itself fires characterData)
// cannot loop, and unrelated page mutations cost only a cheap closest() check.
const observer = new MutationObserver((mutations) => {
  let touched = false;
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement && (node.matches(SELECTOR) || node.querySelector(SELECTOR))) {
        touched = true;
      }
    }
    if (!touched) {
      const target = mutation.target;
      const el = target instanceof HTMLElement ? target : target.parentElement;
      if (el && el.closest(SELECTOR)) touched = true;
    }
    if (touched) break;
  }
  if (touched) resolveAll();
});

function start() {
  resolveAll();
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

// Belt-and-suspenders alongside the observer: a fast first pass on cart:update.
document.addEventListener(ThemeEvents.cartUpdate, resolveAll);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
